/**
 * UserBudgetsPage — Per-user credit budget management
 *
 * Shows:
 *   - Table of org members with their individual budget limits
 *   - Usage vs budget progress bars
 *   - Set/edit budget per user
 *   - Alert thresholds
 *
 * @module packages/billing-admin/src/pages/UserBudgetsPage
 * @since 2026-06-07 (AT-Billing User Budgets)
 */
import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserBudget {
  user_id: string;
  display_name: string;
  email: string;
  budget_cents: number | null; // null = unlimited
  used_cents: number;
  alert_threshold_pct: number; // 0-100
  period: 'monthly' | 'weekly';
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useApiBase(): { baseUrl: string; token: string } {
  const { token, apiBaseUrl } = useAdminAuth();
  return { baseUrl: apiBaseUrl || window.location.origin, token: token ?? '' };
}

function useAllBudgets() {
  const { baseUrl, token } = useApiBase();
  return useQuery({
    queryKey: ['budgets-all'],
    queryFn: async (): Promise<UserBudget[]> => {
      const res = await fetch(`${baseUrl}/admin/budgets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : (data.data ?? []);
    },
    enabled: !!token,
  });
}

function useUserBudgets(customerId: string) {
  const { baseUrl, token } = useApiBase();
  return useQuery({
    queryKey: ['budgets', customerId],
    queryFn: async (): Promise<UserBudget[]> => {
      const res = await fetch(`${baseUrl}/admin/budgets?customerId=${customerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!customerId && !!token,
  });
}

function useSetBudget() {
  const { baseUrl, token } = useApiBase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { customerId: string; userId: string; budgetCents: number | null; alertThresholdPct: number }) => {
      const res = await fetch(`${baseUrl}/admin/budgets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!res.ok) throw new Error(`Set budget failed: ${res.status}`);
      return res.json();
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['budgets', vars.customerId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

function BudgetRow({ user, customerId }: { user: UserBudget; customerId: string }): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [budgetValue, setBudgetValue] = useState(user.budget_cents !== null ? user.budget_cents / 100 : 0);
  const [threshold, setThreshold] = useState(user.alert_threshold_pct);
  const setBudgetMutation = useSetBudget();

  const usedPct = user.budget_cents ? Math.min(100, (user.used_cents / user.budget_cents) * 100) : 0;
  const isOverBudget = user.budget_cents !== null && user.used_cents >= user.budget_cents;
  const isNearThreshold = user.budget_cents !== null && usedPct >= user.alert_threshold_pct;

  function handleSave(): void {
    setBudgetMutation.mutate({
      customerId,
      userId: user.user_id,
      budgetCents: budgetValue > 0 ? budgetValue * 100 : null,
      alertThresholdPct: threshold,
    }, { onSuccess: () => setEditing(false) });
  }

  return (
    <tr className={`border-t border-slate-100 ${isOverBudget ? 'bg-red-50' : ''}`}>
      <td className="px-4 py-3">
        <div>
          <p className="text-sm font-medium text-slate-900">{user.display_name}</p>
          <p className="text-xs text-slate-500">{user.email}</p>
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <input
            type="number"
            min={0}
            value={budgetValue}
            onChange={(e) => setBudgetValue(Number(e.target.value))}
            className="w-24 rounded border border-slate-300 px-2 py-1 text-right text-sm"
          />
        ) : (
          <span className="text-sm font-medium text-slate-900">
            {user.budget_cents !== null ? `$${(user.budget_cents / 100).toFixed(0)}` : 'Unlimited'}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-2 w-24 rounded-full bg-slate-100">
            <div
              className={`h-2 rounded-full ${isOverBudget ? 'bg-red-500' : isNearThreshold ? 'bg-amber-500' : 'bg-cyan-500'}`}
              style={{ width: `${Math.min(100, usedPct)}%` }}
            />
          </div>
          <span className="text-xs text-slate-500">
            ${(user.used_cents / 100).toFixed(0)}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        {editing ? (
          <input
            type="number"
            min={0}
            max={100}
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            className="w-16 rounded border border-slate-300 px-2 py-1 text-center text-sm"
          />
        ) : (
          <span className="text-sm text-slate-600">{user.alert_threshold_pct}%</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <div className="flex gap-1 justify-end">
            <button
              onClick={handleSave}
              disabled={setBudgetMutation.isPending}
              className="rounded bg-cyan-700 px-3 py-1 text-xs font-medium text-white hover:bg-cyan-800"
            >
              Save
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="rounded border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function UserBudgetsPage(): React.JSX.Element {
  const [customerId, setCustomerId] = useState('');
  const allBudgetsQuery = useAllBudgets();
  const filteredQuery = useUserBudgets(customerId);

  const budgets = customerId ? (filteredQuery.data ?? []) : (allBudgetsQuery.data ?? []);
  const isLoading = customerId ? filteredQuery.isLoading : allBudgetsQuery.isLoading;

  return (
    <AdminLayout title="User Budgets">
      <div className="mx-auto max-w-6xl space-y-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">User Budgets</h1>
          <p className="mt-1 text-sm text-slate-500">
            Set per-user credit limits and alert thresholds within an organization
          </p>
        </div>

        {/* Org filter */}
        <div className="rounded-2xl border border-white/70 bg-white/90 p-4 shadow-lg">
          <label className="text-sm font-medium text-slate-700">Filter by Organization UUID (optional)</label>
          <input
            type="text"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value.trim())}
            placeholder="Leave empty to show all budgets..."
            className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 font-mono text-sm"
          />
        </div>

        <div className="rounded-2xl border border-white/70 bg-white/90 shadow-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">User</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Budget</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Usage</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">Alert At</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">Loading...</td>
                </tr>
              )}
              {!isLoading && budgets.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">
                    No user budgets configured yet. Users have unlimited access by default.
                  </td>
                </tr>
              )}
              {budgets.map((user) => (
                <BudgetRow key={user.user_id} user={user} customerId={customerId || 'all'} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
